import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { useState, useRef } from 'react';
import { updateService } from '../services/updateService';
import { useDevices } from '../hooks/useDevices';
import { auth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

interface UpdateApkModalProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
}

const UpdateApkModal: React.FC<UpdateApkModalProps> = ({ open, onClose, deviceId }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { requestUpdate } = useDevices(!!user);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.name.endsWith('.apk')) {
        // Verificar tamanho do arquivo (máximo 100MB)
        if (file.size > 100 * 1024 * 1024) {
          setError('O arquivo é muito grande. O tamanho máximo permitido é 100MB.');
          setSelectedFile(null);
          return;
        }
        setSelectedFile(file);
        setError(null);
      } else {
        setError('Por favor, selecione um arquivo APK válido');
        setSelectedFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    // Verificar autenticação
    if (!auth.currentUser) {
      setError('Você precisa estar autenticado para fazer upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);
    setStatusMessage('Preparando arquivo...');

    try {
      const update = await updateService.uploadApk(
        deviceId, 
        selectedFile,
        (progress) => {
          setUploadProgress(progress);
          
          // Atualizar mensagem de status com base no progresso
          if (progress <= 10) {
            setStatusMessage('Preparando arquivo...');
          } else if (progress <= 30) {
            setStatusMessage('Lendo arquivo...');
          } else if (progress <= 50) {
            setStatusMessage('Iniciando upload...');
          } else if (progress <= 80) {
            setStatusMessage('Enviando arquivo...');
          } else if (progress <= 90) {
            setStatusMessage('Finalizando upload...');
          } else {
            setStatusMessage('Concluído!');
          }
        }
      );
      
      setStatusMessage('Atualizando dispositivo...');
      await requestUpdate(deviceId);
      setStatusMessage('Upload concluído com sucesso!');
      
      // Fechar o modal após um pequeno atraso para mostrar a mensagem de sucesso
      setTimeout(() => {
        onClose();
        setSelectedFile(null);
      }, 1500);
    } catch (error: any) {
      console.error('Erro no upload:', error);
      if (error.message === 'Usuário não está autenticado') {
        setError('Você precisa estar autenticado para fazer upload');
      } else if (error.code === 'storage/unauthorized') {
        setError('Você não tem permissão para fazer upload neste local');
      } else {
        setError(`Erro ao fazer upload do arquivo: ${error.message || 'Tente novamente'}`);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={!uploading ? onClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>Atualizar APK do Dispositivo</DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" gutterBottom>
            Selecione o arquivo APK para atualizar o dispositivo {deviceId}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              mt: 2,
              p: 3,
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 1,
              textAlign: 'center',
              cursor: uploading ? 'not-allowed' : 'pointer',
              ...(!uploading && {
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                }
              })
            }}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".apk"
              style={{ display: 'none' }}
              disabled={uploading}
            />
            <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              {selectedFile ? selectedFile.name : 'Clique para selecionar o APK'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Apenas arquivos .apk são aceitos (máx. 100MB)
            </Typography>
          </Box>

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{statusMessage}</span>
                <span>{uploadProgress.toFixed(0)}%</span>
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 8, borderRadius: 4 }} />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={uploading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          startIcon={<UploadIcon />}
        >
          Fazer Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateApkModal; 